import type { DealForecast } from '@openx/types/dealForecasting.ts';
import type { DealForecastContextualEstimate } from '@openx/types/store/deal.ts';
import { useTranslation } from 'react-i18next';

import { getReductionFactorLabel } from '../utils/getReductionFactorLabel.ts';

type UseGetMappedForecastValuesArgs = {
  audience_monthly_requests?: number | null;
  contextual_estimate?: DealForecastContextualEstimate | null;
  forecast?: DealForecast;
};

type MappedForecastValues = {
  audienceReductionFactorLabel: string;
  contextualImpressionsLabel: string;
  contextualReductionFactorLabel: string;
  requestsWithTargeting: string;
  bidGuidanceHigh: string;
  bidGuidanceLow: string;
  bidGuidanceRecommended: string;
};

export function useGetMappedForecastValues({
  audience_monthly_requests,
  contextual_estimate,
  forecast,
}: UseGetMappedForecastValuesArgs): MappedForecastValues {
  const { t } = useTranslation();

  const { bid_guidance_high, bid_guidance_low, requests_with_targeting, requests_without_targeting } = forecast || {};

  const audienceReductionFactorRequests =
    audience_monthly_requests && requests_without_targeting
      ? audience_monthly_requests / requests_without_targeting
      : 1;

  const contextualMarketRequests = contextual_estimate?.market_requests;
  const contextualReductionFactorRequests =
    contextualMarketRequests && requests_without_targeting ? contextualMarketRequests / requests_without_targeting : 1;

  const reductionFactor = Math.min(audienceReductionFactorRequests, contextualReductionFactorRequests);
  const reducedRequestsWithTargeting =
    requests_with_targeting && requests_with_targeting * Math.min(reductionFactor, 1);

  const audienceReductionFactorLabel = getReductionFactorLabel(
    t,
    audience_monthly_requests,
    requests_without_targeting
  );

  const contextualReductionFactorLabel = getReductionFactorLabel(
    t,
    contextualMarketRequests,
    requests_without_targeting
  );

  const contextualImpressionsLabel =
    contextual_estimate?.impressions != null
      ? Math.round(contextual_estimate.impressions).toLocaleString()
      : t('N / A');

  const requestsWithTargeting = reducedRequestsWithTargeting
    ? Math.round(reducedRequestsWithTargeting).toLocaleString()
    : '0';

  const bidGuidanceLow =
    bid_guidance_low && bid_guidance_low >= 0 ? `< $${(bid_guidance_low - 0.01).toFixed(2)}` : t('N/A');
  const bidGuidanceHigh =
    bid_guidance_high && bid_guidance_high >= 0 ? `> $${(bid_guidance_high + 0.01).toFixed(2)}` : t('N/A');
  const bidGuidanceRecommended =
    bid_guidance_high && bid_guidance_high >= 0 && bid_guidance_low && bid_guidance_low >= 0
      ? `$${bid_guidance_low.toFixed(2)} - $${bid_guidance_high.toFixed(2)}`
      : t('N/A');

  return {
    audienceReductionFactorLabel,
    bidGuidanceHigh,
    bidGuidanceLow,
    bidGuidanceRecommended,
    contextualImpressionsLabel,
    contextualReductionFactorLabel,
    requestsWithTargeting,
  };
}
